import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import SyncResultView from './SyncResultView';
import { creatableKeysFromPlan, impliedContainers } from './flagDisplay';

/**
 * Creates the database row for one flag the catalog already declares.
 *
 * This is not a flag builder. The key, its owner and its removeBy date live in the API's
 * `flag.catalog.ts`, so the only thing this screen can do is give a declared key a row in
 * this environment's database. An undeclared key is a 400 naming it, shown verbatim: the fix
 * is a backend PR, and pretending otherwise would just invite a retry.
 *
 * The candidate list comes from a dry-run sync, because `GET feature-flags` returns rows
 * rather than catalog entries — there is no other way to ask "what is declared but missing".
 * It is a convenience, not a gate: the field stays typeable if the probe fails.
 */
const AddFlagModal = ({
  open,
  existingKeys,
  onDiscover,
  onCreate,
  onApplied,
  onClose,
}) => {
  const [key, setKey] = useState('');
  const [propagate, setPropagate] = useState(false);
  const [reason, setReason] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [discovering, setDiscovering] = useState(false);
  const [discoverFailed, setDiscoverFailed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setKey('');
    setPropagate(false);
    setReason('');
    setError(null);
    setResult(null);
    setCandidates([]);
    setDiscoverFailed(false);
    setDiscovering(true);
    // A read-only probe: dryRun with no keys and no propagation, so nothing is written and
    // no peer is contacted just because someone opened a dialog.
    onDiscover()
      .then((plan) => setCandidates(creatableKeysFromPlan(plan)))
      .catch(() => setDiscoverFailed(true))
      .finally(() => setDiscovering(false));
  }, [open]);

  const trimmedKey = key.trim();
  const alreadyExists = existingKeys.has(trimmedKey);
  const containers = impliedContainers(trimmedKey, existingKeys);

  const submit = () => {
    setSubmitting(true);
    setError(null);
    onCreate({ key: trimmedKey, propagate, reason })
      .then((response) => {
        setResult(response);
        // Refetch immediately: the row exists now, and leaving the tree stale would make a
        // second attempt at the same key look like the right thing to do.
        onApplied(response);
      })
      .catch((err) => setError((err && err.message) || `Could not create ${trimmedKey}`))
      .finally(() => setSubmitting(false));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Add flag
        <Typography variant="body2" color="textSecondary">
          Gives a catalog-declared flag a row in the database. It does not release anything.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {result ? (
          <>
            <Box mb={2}>
              <Alert severity="success">
                <strong>{trimmedKey}</strong> now has a row. It is not visible to anyone yet —
                use Release on the row to change that, in each environment separately.
              </Alert>
            </Box>
            <SyncResultView result={result} />
          </>
        ) : (
          <>
            {error && (
              <Box mb={2}>
                {/* Verbatim: for an undeclared key this names exactly which keys the catalog
                    is missing, which tells the reader a backend PR is needed first. */}
                <Alert severity="error">{error}</Alert>
              </Box>
            )}

            <TextField
              fullWidth
              variant="outlined"
              label="Flag key"
              placeholder="attendance.half_day"
              helperText="Must already exist in the API's flag.catalog.ts."
              value={key}
              onChange={(e) => setKey(e.target.value)}
              inputProps={{ style: { fontFamily: 'monospace' } }}
            />

            {alreadyExists && (
              <Box mt={2}>
                <Alert severity="info">
                  <strong>{trimmedKey}</strong> already has a row in this environment. Creating
                  is a no-op here — if it is missing elsewhere, use Sync catalog with propagation.
                </Alert>
              </Box>
            )}

            <Box mt={2}>
              <Typography variant="caption" color="textSecondary">
                Declared in the catalog, missing here
              </Typography>
              {discovering && (
                <Box display="flex" alignItems="center" style={{ gap: 8 }} mt={1}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="textSecondary">
                    Asking the API for the plan…
                  </Typography>
                </Box>
              )}
              {!discovering && discoverFailed && (
                <Alert severity="info">
                  Could not read the catalog plan, so there are no suggestions. Typing a key
                  still works — an unknown one is rejected with a clear message.
                </Alert>
              )}
              {!discovering && !discoverFailed && candidates.length === 0 && (
                <Typography variant="body2" color="textSecondary">
                  Nothing. Every flag the catalog declares already has a row here.
                </Typography>
              )}
              {!discovering && candidates.length > 0 && (
                <Box display="flex" style={{ gap: 6, flexWrap: 'wrap' }} mt={1}>
                  {candidates.map((candidate) => (
                    <Chip
                      key={candidate}
                      size="small"
                      variant={candidate === trimmedKey ? 'default' : 'outlined'}
                      color={candidate === trimmedKey ? 'primary' : 'default'}
                      label={candidate}
                      onClick={() => setKey(candidate)}
                      style={{ fontFamily: 'monospace' }}
                    />
                  ))}
                </Box>
              )}
            </Box>

            {containers.length > 0 && (
              <Box mt={2}>
                <Alert severity="info">
                  This also creates the namespace node(s){' '}
                  <strong style={{ fontFamily: 'monospace' }}>{containers.join(', ')}</strong>.
                  Containers are levers, not features — putting one into maintenance takes
                  everything beneath it down.
                </Alert>
              </Box>
            )}

            <Box mt={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={propagate}
                    onChange={(e) => setPropagate(e.target.checked)}
                    color="primary"
                  />
                }
                label="Create the row in the other environments too"
              />
              <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
                Rows fan out; release state never does. Each environment computes its own, so a
                flag created from here lands in production OFF — that is the point of the system,
                not a step left undone. There is no "turn it on everywhere".
              </Typography>
            </Box>

            <Box mt={2}>
              <TextField
                fullWidth
                variant="outlined"
                label="Reason (recorded in the release log)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{result ? 'Done' : 'Cancel'}</Button>
        {!result && (
          <Button
            color="primary"
            variant="contained"
            onClick={submit}
            disabled={submitting || !trimmedKey || alreadyExists}>
            {submitting ? 'Creating…' : 'Create row'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddFlagModal;
